"use client"

import { useEffect, useState, useRef } from "react"
import { ResultsActions } from "./results-actions"
import { ArrowRight } from "lucide-react"
import Image from "next/image"
import { getUserCollections, COLLECTIONS } from "@/lib"
import { getCubeImageForPreview } from "@/lib/seasonal-images"
import { Button } from "@/components/ui/button"
import { useRouter, useSearchParams } from "next/navigation"
import type { NFTCollection } from "@/lib/api"
import { motion } from "framer-motion"
import { CollectionInfo } from "./collection-info"
import { useMobile } from "@/hooks/use-mobile"
import { MobileWalletInfo } from "./mobile-wallet-info"
import { withTimeout } from "@/lib/protection"
import Link from "next/link"
import { lookupEnsName } from "@/lib/ens"
import { PAGE_TRANSITIONS } from "@/constants/animations"

import { Progress } from "@/components/ui/progress"
import { StatusAnimation } from "@/components/status-animation"

// Imports for new functionality
import { AnimatedDiceIcon } from "@/components/animated-dice-icon"
import { KeyboardShortcutsTooltip } from "@/components/keyboard-shortcuts-tooltip"
import { HOTKEYS, matchesPhysicalKey } from "@/lib/keyboard-utils"
// import { MobileFloatingActionButton } from "@/components/mobile-floating-action-button"

// Imports for minting buttons
import { useWallet } from '@/contexts/wallet-context';
import MetaMaskButton from "./metamask-button";
import WalletConnectButton from "./WalletConnectButton";
import { GasPriceIndicator } from "./gas-price-indicator";

// Helper function to truncate Ethereum addresses
function truncateAddress(address: string, startChars = 6, endChars = 4): string {
  if (!address) return ""
  if (address.length <= startChars + endChars) return address
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`
}

export function CollectionResults({ walletAddress }: { walletAddress: string }) {
  const [initialFetchedCollections, setInitialFetchedCollections] = useState<NFTCollection[]>([])
  const [currentDisplayedCollections, setCurrentDisplayedCollections] = useState<NFTCollection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({})
  const containerRef = useRef<HTMLDivElement>(null) as unknown as React.RefObject<HTMLDivElement>
  const router = useRouter()
  const searchParams = useSearchParams()
  const [ensName, setEnsName] = useState<string | null>(searchParams.get("ens"))
  const isMobile = useMobile()
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false)
  const [menuHeight, setMenuHeight] = useState(0)
  const [previousCollectionCount, setPreviousCollectionCount] = useState<number | null>(null)
  const [previousTokenCount, setPreviousTokenCount] = useState<number | null>(null)
  const [isLoadingEns, setIsLoadingEns] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(0)
  const [manuallyAddedCollections, setManuallyAddedCollections] = useState<NFTCollection[]>([])

  // State for new functionalities
  const [showDiceButton, setShowDiceButton] = useState(false)
  const [isTowerAnimating, setIsTowerAnimating] = useState(false)
  const [animationType, setAnimationType] = useState<"initial" | "shuffle" | "easterEgg">("initial")
  const [diceIconCount, setDiceIconCount] = useState(6)
  const [userTokenId, setUserTokenId] = useState<number | null>(null)

  // State for minting buttons
  const [connectError, setConnectError] = useState<string | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);

  // Progress tracking states
  const [checkProgress, setCheckProgress] = useState(0);
  const [checkStep, setCheckStep] = useState("");
  const [checkDetails, setCheckDetails] = useState("");
  const [prevCheckStep, setPrevCheckStep] = useState("");

  // EIP1193 Provider hook
  const {
    provider,
    address,
    type,
    connecting,
    error: providerError,
    connectMetaMask,
    connectWalletConnect,
    disconnect,
  } = useWallet();

  // Function to check if wallet has minted totem (for share URL)
  const checkWalletOwnership = async (wallet: string) => {
    try {
      console.log('[CollectionResults] Checking wallet ownership for:', wallet)
      const res = await fetch(`/api/check-totem-ownership?wallet=${wallet}`)
      const data = await res.json()
      console.log('[CollectionResults] Ownership check response:', data)
      if (data.success && data.data.hasTotem && data.data.ownership?.tokenId) {
        setUserTokenId(data.data.ownership.tokenId)
        console.log('[CollectionResults] ✅ Wallet has minted totem, tokenId set to:', data.data.ownership.tokenId)
      } else {
        console.log('[CollectionResults] ℹ️ Wallet has not minted totem yet')
      }
    } catch (error) {
      console.warn('[CollectionResults] ⚠️ Could not check totem ownership:', error)
    }
  }

  // Function to get user's tokenId
  const fetchUserTokenId = async () => {
    console.log('[CollectionResults] fetchUserTokenId called with address:', address, 'type:', type);
    // Fetch tokenId for any connected EIP-1193 provider (MetaMask or WalletConnect)
    if (address) {
      try {
        console.log('[CollectionResults] Calling /api/edit-totem to get tokenId');
        const res = await fetch('/api/edit-totem', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            walletAddress: address,
            collections: currentDisplayedCollections.map(c => c.id)
          }),
        });
        const data = await res.json();
        console.log('[CollectionResults] /api/edit-totem response:', data);
        if (data.success && data.data.tokenId) {
          setUserTokenId(data.data.tokenId);
          console.log('[CollectionResults] User tokenId set to:', data.data.tokenId);
        } else {
          console.warn('[CollectionResults] No tokenId in response or request failed');
        }
      } catch (error) {
        console.warn('[CollectionResults] Could not fetch user tokenId:', error);
      }
    } else {
      console.log('[CollectionResults] fetchUserTokenId skipped - no address or wrong type');
    }
  };

  // Handler for button errors
  const handleButtonError = (error: string) => {
    setMintError(error);
    setTimeout(() => setMintError(null), 5000);
  };

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(window.innerHeight)
    }
    updateViewportHeight()
    window.addEventListener("resize", updateViewportHeight)
    return () => window.removeEventListener("resize", updateViewportHeight)
  }, [])

  useEffect(() => {
    const fetchEnsName = async () => {
      if (!walletAddress || ensName) return
      try {
        setIsLoadingEns(true)
        const name = await lookupEnsName(walletAddress)
        if (name) setEnsName(name)
      } catch (error) {
        console.error("Error fetching ENS name:", error)
      } finally {
        setIsLoadingEns(false)
      }
    }
    fetchEnsName()
  }, [walletAddress, ensName])

  useEffect(() => {
    const handleHeaderMenuChange = (event: CustomEvent) => setIsHeaderMenuOpen(event.detail.isOpen)
    window.addEventListener("headerMenuToggle", handleHeaderMenuChange as EventListener)
    return () => window.removeEventListener("headerMenuToggle", handleHeaderMenuChange as EventListener)
  }, [])

  useEffect(() => {
    const handleMenuHeightChange = (event: CustomEvent) => {
      if (event.detail && event.detail.height) setMenuHeight(event.detail.height)
    }
    window.addEventListener("headerMenuHeightChange", handleMenuHeightChange as EventListener)
    return () => window.removeEventListener("headerMenuHeightChange", handleMenuHeightChange as EventListener)
  }, [])

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener("resize", handleResize)
    window.addEventListener("orientationchange", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("orientationchange", handleResize)
    }
  }, [])

  // Update the useEffect hook that fetches collections to include timeout handling
  useEffect(() => {
    const getCollections = async () => {
      try {
        setLoading(true)
        setError("")
        setCheckProgress(0)
        setCheckStep("Starting collection check...")
        setCheckDetails("Initializing API connections")

        // Use the unified function for consistent data across frontend and API
        const collections = await getUserCollections(walletAddress, (progress) => {
          const percentage = Math.round((progress.current / progress.total) * 100)
          console.log(`Progress update: ${progress.current}/${progress.total} (${percentage}%) - ${progress.collection}`)
          setCheckProgress(percentage)
          setPrevCheckStep(checkStep) // Store previous step before updating
          setCheckStep(progress.collection)
          setCheckDetails("Processing collections...")
        })
        
        setInitialFetchedCollections(collections)
        setCurrentDisplayedCollections(collections.filter((c) => c.owned === true))
        
        // Check if wallet has minted totem for share URL
        checkWalletOwnership(walletAddress)
        
        setCheckProgress(100)
        setPrevCheckStep(checkStep) // Store previous step before final update
        setCheckStep("Complete!")
        setCheckDetails(`Found ${collections.filter(c => c.owned).length} owned collections`)
      } catch (err: any) {
        console.error("Error fetching collections:", err)
        if (err.message && err.message.includes("timed out")) {
          setError(
            "The operation took too long. This may happen with addresses holding many NFTs. Please try again or use a different address.",
          )
        } else {
          setError("Failed to fetch collections. Please try again.")
        }
      } finally {
        setLoading(false)
      }
    }
    getCollections()
  }, [walletAddress])

  // Fetch user tokenId when wallet is connected (MetaMask or WalletConnect)
  useEffect(() => {
    if (address && currentDisplayedCollections.length > 0) {
      console.log('[CollectionResults] Wallet connected, fetching tokenId');
      fetchUserTokenId();
    }
  }, [address, currentDisplayedCollections.length]);

  // Update collection tracking when collections are loaded
  useEffect(() => {
    if (currentDisplayedCollections.length > 0 && walletAddress && !isLoadingEns) {
      // Track collections for analytics or future features
      const totalTokens = currentDisplayedCollections.reduce(
        (sum, collection) => sum + (collection.tokenCount || 0),
        0,
      )
      // Store previous counts for comparison if needed
      setPreviousCollectionCount(currentDisplayedCollections.length)
      setPreviousTokenCount(totalTokens)
    }
  }, [
    currentDisplayedCollections,
    walletAddress,
    isLoadingEns,
    previousCollectionCount,
    previousTokenCount,
  ])


  const handleShuffleTower = (type: "shuffle" | "easterEgg") => {
    if (isTowerAnimating || currentDisplayedCollections.length < 2) return // Need at least 2 to shuffle

    setIsTowerAnimating(true)
    setAnimationType(type)
    setDiceIconCount(Math.floor(Math.random() * 6) + 1)

    // Create a new shuffled array
    const shuffled = [...currentDisplayedCollections].sort(() => 0.5 - Math.random())
    setCurrentDisplayedCollections(shuffled)

    // Use the same animation duration as the main page
    const animationDuration = 800
    setTimeout(() => {
      setIsTowerAnimating(false)
      setAnimationType("initial") // Reset animation type
    }, animationDuration)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }
      if (matchesPhysicalKey(event, HOTKEYS.RANDOMIZE)) {
        // On mobile, allow randomize immediately; on desktop, wait for showDiceButton
        if (isMobile || showDiceButton) {
          event.preventDefault()
          handleShuffleTower("shuffle")
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [showDiceButton, isTowerAnimating, currentDisplayedCollections, isMobile])

  const handleReset = () => router.push("/")

  // Handle image error by tracking which images failed to load
  const handleImageError = (cubeId: string) => {
    console.error(`Failed to load image for cube: ${cubeId}`)
    setImageErrors((prev) => ({ ...prev, [cubeId]: true }))
  }

  const getStartingPosition = () => Math.max(200, viewportHeight / 2)

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="w-full max-w-md p-6 flex flex-col items-center justify-center text-center">
          {/* Progress bar with status */}
          <Progress
            value={checkProgress}
            status={checkStep || "Fetching NFT collections..."}
            className="w-full"
            animateStatus={true}
            showPercentage={true}
          />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center">
        <div className="w-full max-w-md p-6 text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={handleReset} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  // Build the tower from bottom to top
  const buildTowerLayout = () => {
    const towerCubes = [{ id: "base-cube", name: "Base Cube", image: "/images/base-cube.webp", collection: undefined as undefined | NFTCollection }]
    currentDisplayedCollections.forEach((collection) => {
      if (collection.cubeImage) {
        towerCubes.push({
          id: collection.id,
          name: collection.name,
          image: getCubeImageForPreview(collection.id), // Use seasonal image for preview if active
          collection: collection,
        })
      }
    })
    return towerCubes
  }

  const towerLayout = buildTowerLayout()
  const totalCubesInLayout = towerLayout.length
  const cubeSize = isMobile ? 200 : totalCubesInLayout > 5 ? 200 : 224
  const spacingRatio = 0.56

  const getCubePositions = () => {
    return towerLayout.map((_, index) => {
      if (index === 0) return 0
      if (index === 1) return Math.round(cubeSize * spacingRatio)
      if (index === 2) return Math.round(cubeSize * spacingRatio * 2)
      if (index === 3) return Math.round(cubeSize * spacingRatio * 3 + cubeSize * 0.04)
      return Math.round(cubeSize * spacingRatio * index)
    })
  }
  const positions = getCubePositions()
  const towerHeight = positions.length > 0 ? positions[positions.length - 1] + cubeSize : cubeSize

  const getAnimationBatch = (index: number) => {
    // Base cube always appears first
    if (index === 0) return 0
    
    // For small totems (6 or fewer blocks), animate each block sequentially
    if (totalCubesInLayout <= 6) return index
    
    // Determine batch size based on totem size
    let batchSize: number
    if (totalCubesInLayout <= 12) {
      batchSize = 2  // 2 blocks per batch for totems 7-12 blocks
    } else if (totalCubesInLayout <= 24) {
      batchSize = 3  // 3 blocks per batch for totems 13-24 blocks
    } else {
      batchSize = 4  // 4 blocks per batch for totems 25+ blocks
    }
    
    // For larger totems, group blocks into batches for better performance
    // This ensures sequential appearance: batch 1, batch 2, batch 3, etc.
    return Math.floor((index - 1) / batchSize) + 1
  }

  const truncatedAddress = truncateAddress(walletAddress)
  const walletInfoTopPosition = isHeaderMenuOpen ? 72 + menuHeight + 12 : 88

  const defaultSpring = { type: "spring", stiffness: 300, damping: 40, mass: 2, bounce: 0.1 }
  const easterEggSpring = { type: "spring", stiffness: 200, damping: 15, mass: 1, bounce: 0.3 }

  // Framer Motion typing workaround to allow className on motion elements
  const MotionDiv: any = motion.div

  return (
    <div className="flex flex-col flex-1">
              {/* Mobile wallet info - Only visible on mobile */}
        <MotionDiv
          className="md:hidden relative z-30 mb-16 sm:mb-20 pointer-events-auto"
        initial={PAGE_TRANSITIONS.STANDARD.initial}
        animate={PAGE_TRANSITIONS.STANDARD.animate}
        transition={PAGE_TRANSITIONS.STANDARD.transition}
      >
        <div className="w-full">
          <div className="pt-2 ">
            <MobileWalletInfo
              walletAddress={walletAddress}
              truncatedAddress={truncatedAddress}
              ensName={ensName}
              containerRef={containerRef}
              collections={initialFetchedCollections} // Use initial for display consistency here
              handleReset={handleReset}
            />
          </div>
        </div>
      </MotionDiv>


      {/* Mobile NFT Vote Tooltip - Only visible on mobile */}
      {/* showVoteTooltip and setTooltipDismissed are removed, so this block is removed */}

      {/* Fixed wallet info block - positioned directly below header menu */}
      <MotionDiv
        className="fixed left-0 w-full pt-4 px-4 md:px-8 lg:px-12 z-30 hidden md:block pointer-events-none"
        initial={{ top: 88 }}
        animate={{ top: walletInfoTopPosition }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="w-full">
          <div className="bg-gray-100/90 backdrop-blur-sm rounded-[28px] p-6 max-w-[384px] lg:max-w-[384px] pointer-events-auto">
            <div className="mb-4">
              {/* Wallet, ENS and Blocks in grid */}
              <div className="grid grid-cols-2">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Wallet Address</p>
                  <p className="text-sm font-mono truncate" title={walletAddress}>{truncatedAddress}</p>
                </div>
                <div className="min-w-0 border-l border-gray-200 pl-4">
                  {ensName ? (
                    <>
                      <p className="text-xs uppercase tracking-wide text-gray-500">ENS Name</p>
                      <p className="text-sm font-mono truncate">{ensName}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Blocks</p>
                      <p className="text-sm font-mono">{currentDisplayedCollections.length}</p>
                    </>
                  )}
                </div>
              </div>

              {/* Blocks section - spans full width below only when ENS exists */}
              {ensName && (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Blocks</p>
                  <p className="text-sm font-mono">{currentDisplayedCollections.length}</p>
                </div>
              )}
            </div>

            {/* Minting buttons for all screens */}
            <div className="space-y-2">
              {/* Minting buttons - full width for all screens */}
              <div className="flex flex-col gap-2 w-full">
                {(type === 'metamask' || !type) && (
                <MetaMaskButton
                  walletAddress={walletAddress}
                  connectMetaMask={(...args) => connectMetaMask(...args).catch(e => setConnectError(e.message || 'MetaMask error'))}
                  provider={provider}
                  address={address}
                  type={type}
                  connecting={connecting}
                  error={null}
                  className="bg-[#7B39ED] hover:bg-[#6429D8] text-white font-medium w-full h-10"
                  currentCollections={currentDisplayedCollections}
                  tokenId={userTokenId}
                  onError={handleButtonError}
                />)}
                {(type === 'walletconnect' || !type) && (
                <WalletConnectButton
                  connectWalletConnect={(...args) => connectWalletConnect(...args).catch(e => setConnectError(e.message || 'WalletConnect error'))}
                  disconnect={disconnect}
                  provider={provider}
                  address={address}
                  type={type}
                  connecting={connecting}
                  error={null}
                  className="bg-black hover:bg-gray-900 text-white font-medium w-full h-10"
                  currentCollections={currentDisplayedCollections}
                  tokenId={userTokenId}
                  onError={handleButtonError}
                  walletAddress={walletAddress}
                />)}
            </div>
              {/* Error display */}
              {(connectError || mintError) && (
                <div className="w-full text-center text-red-500 text-sm font-normal">
                  {connectError || mintError}
                </div>
              )}
              
              {/* Gas Price Indicator */}
              <div className="w-full flex justify-center mt-2">
                <GasPriceIndicator />
              </div>
            </div>

            {/* Options row */}
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Options</span>
              <div className="flex items-center gap-2">
                {showDiceButton && !isMobile && totalCubesInLayout >= 3 && (
                  <Button
                    variant="outline"
                    className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-100 transition-all duration-300 h-10 px-4 flex items-center gap-2"
                    onClick={() => handleShuffleTower("shuffle")}
                    title="Randomize Tower Order (R)"
                  >
                    <AnimatedDiceIcon dotCount={Math.floor(Math.random() * 6) + 1} />
                    <span className="text-xs font-medium">Shuffle</span>
                  </Button>
                )}
                <ResultsActions
                  containerRef={containerRef}
                  collections={initialFetchedCollections}
                  currentCollections={currentDisplayedCollections}
                  walletAddress={walletAddress}
                  ensName={ensName}
                  isMobile={false}
                  compact
                  tokenId={userTokenId}
                />
              </div>
            </div>
          </div>
        </div>
      </MotionDiv>






      {/* Dice Button removed from floating; now located in wallet info block */}

      {/* Main content */}
      <div className="flex-1 flex flex-col pt-4 md:pt-0">
        {/* Restore the 1:3 layout - left column empty, right column for tower */}
        <div className="grid md:grid-cols-4 gap-8 flex-1">
          {/* Left column - empty space (was for debug info) */}
          <div className="hidden md:block"></div>

          {/* Right column - tower visualization (3 columns) */}
          <div className="md:col-span-3 flex justify-center items-center w-full">
            {/* Removed Card and CardContent components to eliminate border, background, and shadow */}
            <div
              ref={containerRef}
              className={isMobile ? "relative flex items-center justify-center py-8 sm:pt-16 md:pt-8 rounded-2xl" : "relative w-full flex items-center justify-center py-8 sm:pt-16 md:pt-8 rounded-2xl"}
            >
              {/* Desktop Random Button moved near share/download */}
              {currentDisplayedCollections.length === 0 && !loading ? (
                <div className="relative max-w-sm flex flex-col items-center justify-center py-8 rounded-2xl">
                  <div className="text-center mb-8">
                    <h3 className="text-lg font-medium text-gray-800 mb-2">No supported collections found</h3>
                    <p className="text-gray-500 mb-4">This wallet may not own NFTs from our supported collections.</p>
                    <Button asChild className="bg-[#7B39ED] hover:bg-[#6429D8] text-white font-funnel uppercase">
                      <Link href="/collections">
                        Explore Supported Collections <ArrowRight size={16} className="ml-1" />
                      </Link>
                    </Button>
                  </div>
                  <div className="relative" style={{ width: `${cubeSize}px`, height: `${cubeSize}px` }}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Image
                        src="/images/base-cube.webp"
                        alt="Base Cube"
                        width={cubeSize}
                        height={cubeSize}
                        className="object-contain w-auto h-auto"
                        priority
                      />
                    </motion.div>
                  </div>
                </div>
              ) : (
                /* Tower built from bottom to top with proportional spacing and animation */
                <div className="flex items-center justify-center w-full">
                  <div className="relative" style={{ width: `${cubeSize}px`, height: `${towerHeight}px`, marginBottom: `${cubeSize / 2}px` }}>
                    {towerLayout.map((cube, index) => {
                      const batch = getAnimationBatch(index)
                      const isBaseCube = index === 0
                      const currentSpringConfig = animationType === "easterEgg" ? easterEggSpring : defaultSpring

                      const variants = {
                        initial:
                          animationType === "initial"
                            ? isBaseCube
                              ? { bottom: 0, opacity: 1 }
                              : {
                                  bottom: positions[positions.length - 1] + cubeSize * 2 + getStartingPosition(),
                                  opacity: 0,
                                }
                            : { bottom: positions[index], opacity: 1 }, // For shuffles, start from current pos
                        animate: {
                          bottom: positions[index],
                          opacity: 1,
                          x: 0, // Ensure x position is always 0 to stay on primary track
                          transition:
                            animationType === "initial"
                              ? { ...currentSpringConfig, delay: batch * 0.3, opacity: { duration: 0.2 } }
                              : { type: "spring", stiffness: 300, damping: 25, mass: 1.2, opacity: { duration: 0.2 } }, // Match main page spring config
                        },
                      }

                      const imageSrc = imageErrors[cube.id]
                        ? `/placeholder.svg?height=${cubeSize}&width=${cubeSize}&query=${encodeURIComponent(cube.name + " cube")}`
                        : cube.image

                      return (
                        <MotionDiv
                          key={cube.id}
                          layout
                          className="absolute inset-x-0 mx-auto"
                          style={{ width: `${cubeSize}px`, height: `${cubeSize}px`, zIndex: index }}
                          initial="initial"
                          animate="animate"
                          variants={variants}
                          onAnimationComplete={() => {
                            if (animationType === "initial" && index === towerLayout.length - 1 && !showDiceButton) {
                              setShowDiceButton(true)
                            }
                          }}
                        >
                          <CollectionInfo
                            collection={cube.collection || { id: cube.id, name: cube.name, image: cube.image }}
                            isBaseCube={isBaseCube}
                          >
                            <Image
                              src={imageSrc || "/placeholder.svg"}
                              alt={cube.name}
                              width={cubeSize}
                              height={cubeSize}
                              className="object-contain w-auto h-auto"
                              style={{ color: "transparent" }}
                              onError={() => handleImageError(cube.id)}
                              priority={index < 3}
                              crossOrigin="anonymous"
                            />
                          </CollectionInfo>
                        </MotionDiv>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
